/**
 * Geometry builders for the world renderer.
 *
 * Everything is emitted into flat position/colour/uv/index arrays so a whole
 * chunk becomes a handful of merged meshes — the renderer never pays a draw
 * call per building. Each builder records the triangle range it appended, so
 * a raycast hit on the merged mesh maps back to the feature that owns it.
 *
 * Lighting is baked here as vertex colours rather than computed by lights:
 * walls take a fixed sun factor from their facing, which keeps the daytime
 * city reading as sunlit blocks without a real light rig. Walls also carry
 * metre-scaled UVs, so one shared window-grid texture dresses every facade.
 */

import { ShapeUtils, Vector2 } from "three";
import { shade, SUN_AMBIENT, SUN_DIRECT, WALL_BASE_SHADE } from "./palette";

export type Vec2 = readonly [number, number];

/** Metres per degree of latitude; longitude scales by cos(lat) at the origin. */
const M_PER_DEG = 111_320;

/** One facade texture cell is this many metres wide / tall. */
export const WINDOW_W = 3.4;
export const FLOOR_H = 3.1;

/**
 * Sun direction in the xz plane (normalised), shared by wall shading and the
 * projected ground shadows so light and shadow agree.
 */
export const SUN_X = 0.62;
export const SUN_Z = -0.55;

/**
 * Local metre projection around a fixed origin. +x is east, +z is south
 * (Three's default handedness with y up), so north is away from the camera in
 * the home orientation.
 */
export interface Projection {
  toWorld(lngLat: Vec2): [number, number];
  toLngLat(x: number, z: number): [number, number];
}

export function makeProjection(origin: Vec2): Projection {
  const [lon0, lat0] = origin;
  const kx = M_PER_DEG * Math.cos((lat0 * Math.PI) / 180);
  return {
    toWorld: ([lon, lat]) => [(lon - lon0) * kx, -(lat - lat0) * M_PER_DEG],
    toLngLat: (x, z) => [lon0 + x / kx, lat0 - z / M_PER_DEG],
  };
}

/**
 * sRGB → linear for one channel. Buffer colours bypass three's texture
 * decoding but are still linear-to-sRGB encoded on output, so palette values
 * pushed raw come out washed a stop lighter. Converting here means the hex in
 * palette.ts is exactly the pixel on screen.
 */
function toLinear(c: number): number {
  return c < 0.04045 ? c * 0.0773993808 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Growable soup of coloured triangles that becomes one BufferGeometry. */
export class MeshSink {
  positions: number[] = [];
  colors: number[] = [];
  uvs: number[] = [];
  indices: number[] = [];

  get vertexCount(): number {
    return this.positions.length / 3;
  }
  get triangleCount(): number {
    return this.indices.length / 3;
  }

  vertex(x: number, y: number, z: number, rgb: number, u = 0, v = 0): number {
    this.positions.push(x, y, z);
    this.colors.push(
      toLinear(((rgb >> 16) & 0xff) / 255),
      toLinear(((rgb >> 8) & 0xff) / 255),
      toLinear((rgb & 0xff) / 255),
    );
    this.uvs.push(u, v);
    return this.vertexCount - 1;
  }

  triangle(a: number, b: number, c: number): void {
    this.indices.push(a, b, c);
  }
}

/** Triangle range within a merged mesh, mapping hits back to a feature. */
export interface TriRange {
  start: number;
  end: number;
  id: string;
}

/** Triangulate a ring (no holes) into sink triangles at a fixed height. */
export function fillPolygon(
  sink: MeshSink,
  ring: readonly Vec2[],
  proj: Projection,
  y: number,
  rgb: number,
): void {
  const pts = dedupedRing(ring, proj);
  fillPts(sink, pts, y, rgb);
}

function fillPts(sink: MeshSink, pts: ReadonlyArray<[number, number]>, y: number, rgb: number): void {
  if (pts.length < 3) return;
  const tris = ShapeUtils.triangulateShape(
    pts.map(([x, z]) => new Vector2(x, z)),
    [],
  );
  const base = sink.vertexCount;
  for (const [x, z] of pts) sink.vertex(x, y, z, rgb);
  for (const tri of tris) sink.triangle(base + tri[0]!, base + tri[1]!, base + tri[2]!);
}

/** Sunlit shade of a wall colour for a wall segment's outward normal. */
function sunlit(rgb: number, nx: number, nz: number): number {
  return shade(rgb, SUN_AMBIENT + SUN_DIRECT * Math.max(0, nx * SUN_X + nz * SUN_Z));
}

/**
 * Extrude a footprint into walls with window UVs. u runs in facade cells
 * along each wall, v in floors up it, so the shared grid texture lands one
 * window per cell whatever the wall's size. Returns the projected ring so
 * callers can add the roof they want.
 */
export function extrudeWalls(
  sink: MeshSink,
  ring: readonly Vec2[],
  proj: Projection,
  heightM: number,
  wallRgb: number,
): Array<[number, number]> {
  const pts = dedupedRing(ring, proj);
  if (pts.length < 3) return pts;
  const h = Math.max(2.6, heightM);
  const vTop = h / FLOOR_H;

  for (let i = 0; i < pts.length; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[(i + 1) % pts.length]!;
    let nx = bz - az;
    let nz = -(bx - ax);
    const len = Math.hypot(nx, nz) || 1;
    nx /= len;
    nz /= len;
    const top = sunlit(wallRgb, nx, nz);
    const bottom = shade(top, WALL_BASE_SHADE);
    // Whole cells only, so no wall ends on half a window.
    const uEnd = Math.max(1, Math.round(len / WINDOW_W));
    const v0 = sink.vertex(ax, 0, az, bottom, 0, 0);
    const v1 = sink.vertex(bx, 0, bz, bottom, uEnd, 0);
    const v2 = sink.vertex(bx, h, bz, top, uEnd, vTop);
    const v3 = sink.vertex(ax, h, az, top, 0, vTop);
    sink.triangle(v0, v1, v2);
    sink.triangle(v0, v2, v3);
  }
  return pts;
}

/** Flat roof cap over an already projected ring. */
export function flatRoof(
  sink: MeshSink,
  pts: ReadonlyArray<[number, number]>,
  heightM: number,
  rgb: number,
): void {
  fillPts(sink, pts, Math.max(2.6, heightM), rgb);
}

/**
 * Gable roof over a near-rectangular footprint: a ridge along the long axis,
 * two sunlit slopes, and wall-coloured gable triangles closing the ends.
 * Returns false (caller should cap flat) when the ring isn't a quad.
 */
export function gableRoof(
  walls: MeshSink,
  roofs: MeshSink,
  pts: ReadonlyArray<[number, number]>,
  heightM: number,
  roofRgb: number,
  wallRgb: number,
): boolean {
  if (pts.length !== 4) return false;
  const h = Math.max(2.6, heightM);
  const edge = (i: number): number =>
    Math.hypot(pts[(i + 1) % 4]![0] - pts[i]![0], pts[(i + 1) % 4]![1] - pts[i]![1]);
  // Long sides host the eaves; the ridge joins the short sides' midpoints.
  const startShort = edge(0) + edge(2) < edge(1) + edge(3) ? 0 : 1;
  const s0 = pts[startShort]!;
  const s0b = pts[(startShort + 1) % 4]!;
  const s1 = pts[(startShort + 2) % 4]!;
  const s1b = pts[(startShort + 3) % 4]!;
  const shortLen = Math.min(edge(startShort), edge((startShort + 2) % 4));
  const rise = Math.min(3.6, shortLen * 0.4);
  const ridgeY = h + rise;
  const m0: [number, number] = [(s0[0] + s0b[0]) / 2, (s0[1] + s0b[1]) / 2];
  const m1: [number, number] = [(s1[0] + s1b[0]) / 2, (s1[1] + s1b[1]) / 2];

  // Two slopes, each an eave edge up to the ridge.
  for (const [ea, eb, ra, rb] of [
    [s0b, s1, m0, m1],
    [s1b, s0, m1, m0],
  ] as const) {
    let nx = eb[1] - ea[1];
    let nz = -(eb[0] - ea[0]);
    const len = Math.hypot(nx, nz) || 1;
    const colour = sunlit(roofRgb, nx / len, nz / len);
    const v0 = roofs.vertex(ea[0], h, ea[1], colour);
    const v1 = roofs.vertex(eb[0], h, eb[1], colour);
    const v2 = roofs.vertex(rb[0], ridgeY, rb[1], colour);
    const v3 = roofs.vertex(ra[0], ridgeY, ra[1], colour);
    roofs.triangle(v0, v1, v2);
    roofs.triangle(v0, v2, v3);
  }

  // Gable ends, in wall colour, no windows (uv pinned to a plain cell edge).
  for (const [ga, gb, gm] of [
    [s0, s0b, m0],
    [s1, s1b, m1],
  ] as const) {
    let nx = gb[1] - ga[1];
    let nz = -(gb[0] - ga[0]);
    const len = Math.hypot(nx, nz) || 1;
    const colour = sunlit(wallRgb, nx / len, nz / len);
    const v0 = walls.vertex(ga[0], h, ga[1], colour, 0, 0);
    const v1 = walls.vertex(gb[0], h, gb[1], colour, 0, 0);
    const v2 = walls.vertex(gm[0], ridgeY, gm[1], colour, 0, 0);
    walls.triangle(v0, v1, v2);
  }
  return true;
}

/**
 * Ground shadow of an extruded footprint: each wall edge swept along the
 * shadow offset, plus the whole footprint at the offset — together the exact
 * flat-ground projection of the prism. Drawn translucent above the roads.
 */
export function projectedShadow(
  sink: MeshSink,
  pts: ReadonlyArray<[number, number]>,
  heightM: number,
  y: number,
  rgb: number,
): void {
  if (pts.length < 3) return;
  const h = Math.max(2.6, heightM);
  // Shadow falls away from the sun, foreshortened so towers don't blanket
  // whole blocks from the game's aerial camera.
  const ox = -SUN_X * h * 0.55;
  const oz = -SUN_Z * h * 0.55;
  for (let i = 0; i < pts.length; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[(i + 1) % pts.length]!;
    const v0 = sink.vertex(ax, y, az, rgb);
    const v1 = sink.vertex(bx, y, bz, rgb);
    const v2 = sink.vertex(bx + ox, y, bz + oz, rgb);
    const v3 = sink.vertex(ax + ox, y, az + oz, rgb);
    sink.triangle(v0, v1, v2);
    sink.triangle(v0, v2, v3);
  }
  fillPts(
    sink,
    pts.map(([x, z]) => [x + ox, z + oz] as [number, number]),
    y,
    rgb,
  );
}

/**
 * Append a polyline ribbon of the given half width, with round-ish joints.
 * `rgbAt` overrides the flat colour per centre-line vertex — the foot-traffic
 * overlay uses it to run a smooth busyness gradient along each street.
 */
export function ribbon(
  sink: MeshSink,
  line: readonly Vec2[],
  proj: Projection,
  halfWidth: number,
  y: number,
  rgb: number,
  rgbAt?: (index: number) => number,
): void {
  const colour = (i: number): number => (rgbAt ? rgbAt(i) : rgb);
  const pts = line.map((p) => proj.toWorld(p));
  for (let i = 0; i < pts.length - 1; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[i + 1]!;
    let dx = bx - ax;
    let dz = bz - az;
    const len = Math.hypot(dx, dz);
    if (len < 0.01) continue;
    dx /= len;
    dz /= len;
    const px = -dz * halfWidth;
    const pz = dx * halfWidth;
    const ca = colour(i);
    const cb = colour(i + 1);
    const v0 = sink.vertex(ax + px, y, az + pz, ca);
    const v1 = sink.vertex(ax - px, y, az - pz, ca);
    const v2 = sink.vertex(bx - px, y, bz - pz, cb);
    const v3 = sink.vertex(bx + px, y, bz + pz, cb);
    sink.triangle(v0, v1, v2);
    sink.triangle(v0, v2, v3);
  }
  // Discs at interior joints hide the seams between segments.
  const SEGS = 8;
  for (let i = 1; i < pts.length - 1; i++) {
    const [cx, cz] = pts[i]!;
    const cj = colour(i);
    const centre = sink.vertex(cx, y, cz, cj);
    const first = sink.vertexCount;
    for (let s = 0; s < SEGS; s++) {
      const a = (s / SEGS) * Math.PI * 2;
      sink.vertex(cx + Math.cos(a) * halfWidth, y, cz + Math.sin(a) * halfWidth, cj);
    }
    for (let s = 0; s < SEGS; s++) {
      sink.triangle(centre, first + s, first + ((s + 1) % SEGS));
    }
  }
}

/**
 * Painted centre-line dashes along a road. Same maths as `ribbon`, but broken
 * into short strips so the paint reads as paint from the air.
 */
export function laneDashes(
  sink: MeshSink,
  line: readonly Vec2[],
  proj: Projection,
  y: number,
  rgb: number,
): void {
  const DASH = 3;
  const GAP = 4;
  const HALF_W = 0.22;
  const pts = line.map((p) => proj.toWorld(p));
  let carry = 0; // distance into the dash/gap cycle at the segment start
  for (let i = 0; i < pts.length - 1; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[i + 1]!;
    let dx = bx - ax;
    let dz = bz - az;
    const len = Math.hypot(dx, dz);
    if (len < 0.01) continue;
    dx /= len;
    dz /= len;
    const px = -dz * HALF_W;
    const pz = dx * HALF_W;
    let at = carry > 0 ? -carry : 0;
    while (at < len) {
      const d0 = Math.max(0, at);
      const d1 = Math.min(len, at + DASH);
      if (d1 > d0) {
        const x0 = ax + dx * d0;
        const z0 = az + dz * d0;
        const x1 = ax + dx * d1;
        const z1 = az + dz * d1;
        const v0 = sink.vertex(x0 + px, y, z0 + pz, rgb);
        const v1 = sink.vertex(x0 - px, y, z0 - pz, rgb);
        const v2 = sink.vertex(x1 - px, y, z1 - pz, rgb);
        const v3 = sink.vertex(x1 + px, y, z1 + pz, rgb);
        sink.triangle(v0, v1, v2);
        sink.triangle(v0, v2, v3);
      }
      at += DASH + GAP;
    }
    carry = (len - (at - DASH - GAP)) % (DASH + GAP);
  }
}

/** A stylised tree: a squat trunk under a low cone of canopy. */
export function tree(
  sink: MeshSink,
  x: number,
  z: number,
  scale: number,
  canopyRgb: number,
  trunkRgb: number,
): void {
  const SEGS = 6;
  const trunkH = 1.6 * scale;
  const canopyH = 5.2 * scale;
  const canopyR = 2.6 * scale;
  const trunkR = 0.4 * scale;
  for (const [r, y0, y1, rgb] of [
    [trunkR, 0, trunkH, trunkRgb],
    [canopyR, trunkH, trunkH + canopyH, canopyRgb],
  ] as const) {
    const tip = sink.vertex(x, y1, z, rgb);
    const first = sink.vertexCount;
    for (let s = 0; s < SEGS; s++) {
      const a = (s / SEGS) * Math.PI * 2;
      // Cheap facing light: east-side vertices a touch brighter.
      const lit = shade(rgb, 0.85 + 0.15 * Math.max(0, Math.cos(a)));
      sink.vertex(x + Math.cos(a) * r, y0, z + Math.sin(a) * r, lit);
    }
    for (let s = 0; s < SEGS; s++) {
      sink.triangle(tip, first + s, first + ((s + 1) % SEGS));
    }
  }
}

/** Line-segment soup for outlines, one colour per outline. */
export class LineSink {
  positions: number[] = [];
  colors: number[] = [];

  segment(ax: number, ay: number, az: number, bx: number, by: number, bz: number, rgb: number): void {
    const r = toLinear(((rgb >> 16) & 0xff) / 255);
    const g = toLinear(((rgb >> 8) & 0xff) / 255);
    const b = toLinear((rgb & 0xff) / 255);
    this.positions.push(ax, ay, az, bx, by, bz);
    this.colors.push(r, g, b, r, g, b);
  }

  ring(ring: readonly Vec2[], proj: Projection, y: number, rgb: number): void {
    const pts = dedupedRing(ring, proj);
    for (let i = 0; i < pts.length; i++) {
      const [ax, az] = pts[i]!;
      const [bx, bz] = pts[(i + 1) % pts.length]!;
      this.segment(ax, y, az, bx, y, bz, rgb);
    }
  }
}

/**
 * Project a ring, drop a duplicated closing point and degenerate slivers, and
 * normalise winding to counter-clockwise in the xz plane so wall normals and
 * cap triangulation are consistent.
 */
export function dedupedRing(ring: readonly Vec2[], proj: Projection): Array<[number, number]> {
  const pts = ring.map((p) => proj.toWorld(p));
  if (pts.length > 1) {
    const [fx, fz] = pts[0]!;
    const [lx, lz] = pts[pts.length - 1]!;
    if (Math.abs(fx - lx) < 0.01 && Math.abs(fz - lz) < 0.01) pts.pop();
  }
  if (pts.length < 3) return pts;
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[(i + 1) % pts.length]!;
    area += ax * bz - bx * az;
  }
  // ShapeUtils treats y-down CCW; with z as y this means negative signed area.
  if (area > 0) pts.reverse();
  return pts;
}

/** Centroid of a ring, good enough to hang labels and couriers on. */
export function ringCentre(ring: readonly Vec2[]): [number, number] {
  let lon = 0;
  let lat = 0;
  for (const [x, y] of ring) {
    lon += x / ring.length;
    lat += y / ring.length;
  }
  return [lon, lat];
}
